import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ThumbsUp, ThumbsDown, Save, Star, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { behaviorAPI } from '../utils/api';

const BEHAVIOR_SLOTS = [0, 1, 2, 3, 4, 5, 6]; // 7 slots

const BehaviorSlot = ({ position, type, behavior, template, saving, onInputChange, onSave }) => {
  const isPositive = type === 'positive';
  const hasTemplate = !!template;
  
  return (
    <div 
      className={`bg-white rounded-xl p-4 border-2 ${hasTemplate ? (isPositive ? 'border-green-300' : 'border-red-300') : 'border-slate-200'}`}
    >
      <div className="flex items-center gap-4 mb-3">
        <div className={`px-3 py-1 rounded-lg ${isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} font-bold text-sm`}>
          #{position + 1}
        </div>
        {hasTemplate && (
          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            ✓ Configurado
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-3">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Nome</label>
          <Input
            data-testid={`behavior-name-${type}-${position}`}
            value={behavior.name || ''}
            onChange={(e) => onInputChange('name', e.target.value)}
            placeholder={isPositive ? 'Ex: Participou da aula' : 'Ex: Conversou fora de hora'}
            className="h-10"
          />
        </div>
        
        <div className="md:col-span-5">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Descrição</label>
          <Textarea
            data-testid={`behavior-description-${type}-${position}`}
            value={behavior.description || ''}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Descrição opcional..."
            className="h-10 min-h-[40px] resize-none"
            rows={1}
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-slate-700 mb-1 block flex items-center gap-1">
            {isPositive ? (
              <>
                <Star className="w-3 h-3 text-yellow-500" />
                AURA
              </>
            ) : (
              <>
                <Heart className="w-3 h-3 text-red-500" />
                HP
              </>
            )}
          </label>
          <Input
            data-testid={`behavior-value-${type}-${position}`}
            type="number"
            value={isPositive ? (behavior.xp_reward || 0) : (behavior.hp_penalty || 0)}
            onChange={(e) => onInputChange(isPositive ? 'xp_reward' : 'hp_penalty', e.target.value)}
            min={1}
            max={100}
            className="h-10"
          />
        </div>
        
        <div className="md:col-span-2">
          <Button
            data-testid={`save-behavior-${type}-${position}`}
            onClick={onSave}
            disabled={saving || !behavior.name?.trim()}
            className={`w-full h-10 ${isPositive ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? '...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminBehaviors = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({ positive: {}, negative: {} });
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('positive');
  const [positiveBehaviors, setPositiveBehaviors] = useState({});
  const [negativeBehaviors, setNegativeBehaviors] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    loadTemplates();
  }, []);

  const initializeBehaviors = (data) => {
    const positive = {};
    const negative = {};
    
    BEHAVIOR_SLOTS.forEach(position => {
      positive[position] = data.positive?.[position] || {
        name: '',
        description: '',
        xp_reward: 10,
        hp_penalty: 0
      };
      negative[position] = data.negative?.[position] || {
        name: '',
        description: '',
        xp_reward: 0,
        hp_penalty: 5
      };
    });
    
    setPositiveBehaviors(positive);
    setNegativeBehaviors(negative);
  };

  const loadTemplates = async () => {
    try {
      const response = await behaviorAPI.getTemplates();
      setTemplates(response.data);
      initializeBehaviors(response.data);
    } catch (error) {
      console.error('Failed to load behavior templates:', error);
      toast.error('Erro ao carregar templates de comportamentos');
      initializeBehaviors({ positive: {}, negative: {} });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (type, position, field, value) => {
    const setter = type === 'positive' ? setPositiveBehaviors : setNegativeBehaviors;
    setter(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        [field]: (field === 'xp_reward' || field === 'hp_penalty') ? parseInt(value) || 0 : value
      }
    }));
  };

  const handleSaveBehavior = async (type, position) => {
    const behaviors = type === 'positive' ? positiveBehaviors : negativeBehaviors;
    const behavior = behaviors[position];
    
    if (!behavior.name?.trim()) {
      toast.error('Nome do comportamento é obrigatório');
      return;
    }
    
    setSaving(prev => ({ ...prev, [`${type}-${position}`]: true }));
    
    try {
      await behaviorAPI.updateTemplate(type, position, {
        name: behavior.name,
        description: behavior.description || '',
        xp_reward: type === 'positive' ? (behavior.xp_reward || 10) : 0,
        hp_penalty: type === 'negative' ? (behavior.hp_penalty || 5) : 0
      });
      toast.success(`Comportamento "${behavior.name}" salvo!`);
      loadTemplates();
    } catch (error) {
      console.error('Failed to save behavior:', error);
      toast.error('Erro ao salvar comportamento');
    } finally {
      setSaving(prev => ({ ...prev, [`${type}-${position}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  const currentBehaviors = selectedType === 'positive' ? positiveBehaviors : negativeBehaviors;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/teacher/dashboard')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="admin-behaviors-title">
            Configurar Comportamentos
          </h1>
          <p className="text-slate-600 font-nunito">
            Defina comportamentos positivos (recompensam AURA) e negativos (penalizam HP) para aplicar aos alunos.
          </p>
        </div>

        {/* Type Tabs */}
        <div className="mb-6">
          <div className="flex gap-2">
            <Button
              data-testid="positive-tab"
              onClick={() => setSelectedType('positive')}
              variant={selectedType === 'positive' ? 'default' : 'outline'}
              className={`flex items-center gap-2 ${selectedType === 'positive' ? 'bg-green-600 hover:bg-green-700' : 'text-green-600 border-green-200'}`}
            >
              <ThumbsUp className="w-4 h-4" />
              Positivos
            </Button>
            <Button
              data-testid="negative-tab"
              onClick={() => setSelectedType('negative')}
              variant={selectedType === 'negative' ? 'default' : 'outline'}
              className={`flex items-center gap-2 ${selectedType === 'negative' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200'}`}
            >
              <ThumbsDown className="w-4 h-4" />
              Negativos
            </Button>
          </div>
        </div>

        {/* Positive Behaviors */}
        {selectedType === 'positive' && (
          <Card className="p-6 bg-green-50 border-green-200 border-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-green-100 border-green-300 border-2 flex items-center justify-center">
                <ThumbsUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-outfit font-bold text-green-700">
                  Comportamentos Positivos
                </h2>
                <p className="text-sm text-slate-600">
                  7 comportamentos que recompensam AURA ao aluno
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {BEHAVIOR_SLOTS.map((position) => (
                <BehaviorSlot
                  key={`positive-${position}`}
                  position={position}
                  type="positive"
                  behavior={positiveBehaviors[position] || {}}
                  template={templates.positive?.[position]}
                  saving={saving[`positive-${position}`]}
                  onInputChange={(field, value) => handleInputChange('positive', position, field, value)}
                  onSave={() => handleSaveBehavior('positive', position)}
                />
              ))}
            </div>
          </Card>
        )}

        {/* Negative Behaviors */}
        {selectedType === 'negative' && (
          <Card className="p-6 bg-red-50 border-red-200 border-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-red-100 border-red-300 border-2 flex items-center justify-center">
                <ThumbsDown className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-outfit font-bold text-red-700">
                  Comportamentos Negativos
                </h2>
                <p className="text-sm text-slate-600">
                  7 comportamentos que penalizam HP do aluno
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {BEHAVIOR_SLOTS.map((position) => (
                <BehaviorSlot
                  key={`negative-${position}`}
                  position={position}
                  type="negative"
                  behavior={negativeBehaviors[position] || {}}
                  template={templates.negative?.[position]}
                  saving={saving[`negative-${position}`]}
                  onInputChange={(field, value) => handleInputChange('negative', position, field, value)}
                  onSave={() => handleSaveBehavior('negative', position)}
                />
              ))}
            </div>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mt-6 p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div>
              <h3 className="font-bold text-blue-800">Como usar?</h3>
              <p className="text-sm text-blue-700">
                Após configurar os comportamentos, você verá botões "Positivo" e "Negativo" no card de cada aluno. 
                Ao clicar, uma lista dos comportamentos configurados aparecerá para você aplicar. 
                O aluno receberá uma notificação automática sobre a ação.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminBehaviors;
